import { useMemo, useState } from 'react'
import { Button, Chip, Dropdown, Table } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { ConfirmDialog } from '@/components/ui/form-modal'
import { MoreIcon, PlusIcon } from '@/components/ui/icons'

import {
  findMenuChoice,
  useMenuChoices,
  useRecipeModifierActions,
  useRecipeModifiers,
  useRecipeOptions,
  useStockItemsById,
} from '../hooks'
import type { MenuChoiceGroup } from '../hooks'
import type { RecipeModifier, StockItem } from '../types'
import { modifierModeLabels, recipeQuantity } from './inventory-format'
import { RecipeModifierFormDialog } from './recipe-modifier-form-dialog'

const PAGE_SIZE = 10

/**
 * The page's chosen recipe, or the first one when nothing has been chosen yet —
 * so the tab opens on a recipe's modifiers rather than on an empty table.
 * Derived, not stored: the default follows the list as it loads.
 */
function useSelectedRecipeId(chosen: string | null): string | null {
  const { recipes } = useRecipeOptions()
  return chosen ?? recipes[0]?.id ?? null
}

interface RecipeModifiersPanelProps {
  /** The recipe whose modifiers show, or `null` for "the first one". */
  recipeId: string | null
  /** Moves the tab to a recipe — the one a new modifier was just added to. */
  onRecipeChange: (recipeId: string) => void
}

/**
 * The choices on a recipe — an extra shot, oat milk instead of dairy — and what
 * each does to the stock a sale uses.
 *
 * The form for a new modifier opens on the recipe shown and can move to
 * another; the tab follows it on save, so the new modifier is in view. An
 * existing one is edited in the same form, on its own recipe.
 *
 * The Menu column is the check that matters for sales: a modifier only reaches
 * a sale through the menu choice it is linked to.
 */
export function RecipeModifiersPanel({
  recipeId: chosen,
  onRecipeChange,
}: RecipeModifiersPanelProps) {
  const recipeId = useSelectedRecipeId(chosen)
  const [page, setPage] = useState(0)
  const [isFormOpen, setIsFormOpen] = useState(false)
  /** The modifier the form edits, or `null` when it adds one. */
  const [editing, setEditing] = useState<RecipeModifier | null>(null)
  /** Bumped on each opening, so the form mounts fresh rather than holding the last draft. */
  const [formSession, setFormSession] = useState(0)
  const [pendingDelete, setPendingDelete] = useState<RecipeModifier | null>(null)

  const { remove } = useRecipeModifierActions()
  const { groups: menuGroups, isLoading: isLoadingMenu } = useMenuChoices()

  function openForm(modifier: RecipeModifier | null = null) {
    setEditing(modifier)
    setFormSession((session) => session + 1)
    setIsFormOpen(true)
  }

  function confirmDelete(modifier: RecipeModifier) {
    remove.reset()
    setPendingDelete(modifier)
  }
  /** The page belongs to the recipe it was turned for; a new recipe starts on its first. */
  const [pageOf, setPageOf] = useState(recipeId)

  if (pageOf !== recipeId) {
    setPageOf(recipeId)
    setPage(0)
  }

  const { recipes, isLoading: isLoadingRecipes } = useRecipeOptions()
  const { modifiers, total, isLoading, isRefreshing, error, retry } = useRecipeModifiers(
    recipeId,
    page,
    PAGE_SIZE,
  )

  const itemIds = useMemo(
    () => [
      ...new Set(
        modifiers.flatMap((modifier) => modifier.components.map((part) => part.stockItemId)),
      ),
    ],
    [modifiers],
  )
  const items = useStockItemsById(itemIds)
  const recipe = recipes.find((candidate) => candidate.id === recipeId) ?? null

  return (
    <>
      <ModuleIntro
        description="The choices on a recipe — an extra shot, oat milk instead of dairy — and what each one adds to or takes from the stock a sale uses."
        action={
          recipe && (
            <Button
              variant="primary"
              size="sm"
              className="h-9 rounded-full px-4 font-medium"
              onPress={() => openForm()}
            >
              <PlusIcon width={16} height={16} />
              New modifier
            </Button>
          )
        }
      />

      {isLoadingRecipes ? (
        <LoadingState />
      ) : recipes.length === 0 ? (
        <EmptyState
          title="No recipes yet."
          description="Modifiers belong to a recipe. Once an item made from other items has its recipe, its modifiers show here."
        />
      ) : isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="this recipe’s modifiers" error={error} onRetry={retry} />
      ) : modifiers.length === 0 ? (
        <EmptyState
          title="No modifiers on this recipe."
          description="A modifier changes what a sale of this recipe uses — an extra shot adds coffee, oat milk swaps out the dairy."
          action={{ label: 'New modifier', onPress: () => openForm() }}
        />
      ) : (
        <>
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <ModifiersTable
              modifiers={modifiers}
              items={items}
              menuGroups={menuGroups}
              isLoadingMenu={isLoadingMenu}
              busyId={remove.isPending ? (remove.variables?.modifierId ?? null) : null}
              onEdit={openForm}
              onDelete={confirmDelete}
            />
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      {recipe && (
        <RecipeModifierFormDialog
          key={formSession}
          isOpen={isFormOpen}
          onOpenChange={setIsFormOpen}
          recipes={recipes}
          defaultRecipeId={recipe.id}
          modifier={editing}
          onSaved={(savedTo) => {
            setIsFormOpen(false)
            onRecipeChange(savedTo)
          }}
        />
      )}

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onOpenChange={(isOpen) => !isOpen && setPendingDelete(null)}
        title="Delete this modifier?"
        confirmLabel="Delete"
        isBusy={remove.isPending}
        error={remove.error}
        errorFallback="Could not delete this modifier."
        onConfirm={() => {
          if (!pendingDelete) return
          remove.mutate(
            { recipeId: pendingDelete.recipeId, modifierId: pendingDelete.id },
            { onSuccess: () => setPendingDelete(null) },
          )
        }}
        body={
          <>
            <span className="text-foreground font-medium">{pendingDelete?.name}</span> comes off{' '}
            {recipe?.name ?? 'this recipe'}. Sales that pick its menu choice no longer change the
            stock they use.
          </>
        }
      />
    </>
  )
}

/** `Milk · Oat milk`, or why there is no link to show. */
function MenuCell({
  modifier,
  menuGroups,
  isLoadingMenu,
}: {
  modifier: RecipeModifier
  menuGroups: readonly MenuChoiceGroup[]
  isLoadingMenu: boolean
}) {
  const ref = modifier.catalogModifierRef

  if (!ref) {
    return (
      <Chip size="sm" variant="soft" color="warning">
        Not linked
      </Chip>
    )
  }

  if (isLoadingMenu) return <span className="text-muted">…</span>

  const found = findMenuChoice(menuGroups, ref)
  if (!found) {
    return (
      <Chip size="sm" variant="soft" color="danger">
        Not on the menu
      </Chip>
    )
  }

  return (
    <span className="text-sm">
      <span className="text-muted">{found.group} · </span>
      {found.choice.name}
    </span>
  )
}

/** `+ 0.03 L Oat milk`, `− 0.2 L Whole milk` — what one component does. */
function componentLine(
  part: RecipeModifier['components'][number],
  items: ReadonlyMap<string, StockItem>,
): string {
  const sign = part.effectType === 'ADD' ? '+' : '−'
  const name = items.get(part.stockItemId)?.name ?? '…'

  return `${sign} ${recipeQuantity(part.quantity)} ${part.unitOfMeasure} ${name}`
}

interface ModifiersTableProps {
  modifiers: RecipeModifier[]
  items: ReadonlyMap<string, StockItem>
  menuGroups: readonly MenuChoiceGroup[]
  isLoadingMenu: boolean
  /** The modifier being deleted, whose actions wait for it. */
  busyId: string | null
  onEdit: (modifier: RecipeModifier) => void
  onDelete: (modifier: RecipeModifier) => void
}

function ModifiersTable({
  modifiers,
  items,
  menuGroups,
  isLoadingMenu,
  busyId,
  onEdit,
  onDelete,
}: ModifiersTableProps) {
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label="Recipe modifiers">
          <Table.Header>
            <Table.Column id="name" isRowHeader>
              Modifier
            </Table.Column>
            <Table.Column id="menu">Menu choice</Table.Column>
            <Table.Column id="effect">Effect</Table.Column>
            <Table.Column id="components">Stock used</Table.Column>
            <Table.Column id="status">Status</Table.Column>
            <Table.Column id="actions" className="w-12">
              <span className="sr-only">Actions</span>
            </Table.Column>
          </Table.Header>

          <Table.Body>
            {modifiers.map((modifier) => (
              <Table.Row key={modifier.id} id={modifier.id}>
                <Table.Cell>
                  <span className="font-medium">{modifier.name}</span>
                </Table.Cell>

                <Table.Cell>
                  <MenuCell
                    modifier={modifier}
                    menuGroups={menuGroups}
                    isLoadingMenu={isLoadingMenu}
                  />
                </Table.Cell>

                <Table.Cell>
                  <span className="text-muted">
                    {modifier.affectsStock && modifier.mode
                      ? modifierModeLabels[modifier.mode]
                      : 'No effect on stock'}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  {modifier.components.length ? (
                    <span className="flex flex-col gap-0.5 text-sm tabular-nums">
                      {modifier.components.map((part) => (
                        <span key={`${part.stockItemId}-${part.effectType}`}>
                          {componentLine(part, items)}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </Table.Cell>

                <Table.Cell>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={modifier.status === 'ACTIVE' ? 'success' : 'default'}
                  >
                    {modifier.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                  </Chip>
                </Table.Cell>

                <Table.Cell>
                  <Dropdown>
                    {/* `Dropdown.Trigger` is itself a button — wrapping one inside it
                        nests two, which React rejects outright. */}
                    <Dropdown.Trigger
                      aria-label={`Actions for ${modifier.name}`}
                      isDisabled={busyId === modifier.id}
                      className="text-muted hover:text-foreground hover:bg-surface inline-flex size-8 items-center justify-center rounded-full transition-colors disabled:opacity-50"
                    >
                      <MoreIcon width={16} height={16} />
                    </Dropdown.Trigger>
                    <Dropdown.Popover>
                      <Dropdown.Menu
                        aria-label="Modifier actions"
                        onAction={(key) => (key === 'edit' ? onEdit(modifier) : onDelete(modifier))}
                      >
                        <Dropdown.Item id="edit">Edit</Dropdown.Item>
                        <Dropdown.Item id="delete">Delete</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}
